/**
 * A releitura localizada da ficha (E155, GUS-192, ADR-0105). Espelha
 * `apps/api/types/releitura_da_leitura.py`.
 *
 * - `POST /casos/:id/ficha-da-leitura/releituras`: a equipe pede que um campo seja lido de novo
 *   na fonte (`campo` + `motivo`) ou confirma o valor como está impresso, com o documento e a
 *   página (`valor` + `documento_id` [+ `pagina`]). A resposta diz o resultado (`mudou`,
 *   `sem_mudanca`, `ambigua`, `nao_localizado`), a fonte e quantas peças foram verificadas de novo.
 * - `POST .../releituras/documento` (E155.F2): todos os campos esperados num documento, de uma vez.
 * - `GET .../releituras`: as releituras do caso.
 *
 * O valor confirmado entra na entrada do exame com origem e autoria; a entrega anterior é
 * preservada. A recusa do servidor chega tipada, para a tela dizer o motivo.
 */
import axios, { type AxiosInstance } from 'axios';

import type { CampoDaFichaId, PaginaNaFonte } from '@/lib/ficha_da_leitura';

export type OrigemDaReleitura = 'releitura' | 'correcao_humana';
export type ResultadoDaReleitura = 'mudou' | 'sem_mudanca' | 'ambigua' | 'nao_localizado';

export const RELEITURAS_MAX_POR_CAMPO = 3;
export const MOTIVO_MINIMO = 15;
export const MOTIVO_MAXIMO = 500;
/** O instante da assinatura não se relê pelo literal: é conferido com os eventos e horários. */
export const CAMPOS_SEM_RELEITURA: readonly CampoDaFichaId[] = ['instante_da_assinatura'];

export interface PedidoDeReleituraData {
  campo: CampoDaFichaId;
  referente_id: string | null;
  motivo: string;
  valor?: string | null;
  documento_id?: string | null;
  pagina?: number | null;
}

export interface FonteDaReleituraData {
  documento_id: string;
  documento_nome: string;
  pagina: number | null;
  original_id: string;
  pagina_no_original: number | null;
  rotulo: string | null;
  trecho: string | null;
  localizado: boolean;
  origem_do_texto: 'camada' | 'imagem' | null;
}

export interface CandidataDaReleituraData {
  valor: string;
  literal: string | null;
  fonte: FonteDaReleituraData;
}

export interface ConteudoDaReleituraData {
  antes: string | null;
  depois: string | null;
  literal: string | null;
  fonte: FonteDaReleituraData | null;
  candidatas: CandidataDaReleituraData[];
  motivo: string;
  pecas_reabertas: string[];
}

export interface ReleituraDaLeituraData {
  id: string;
  case_id: string;
  campo: CampoDaFichaId;
  referente_id: string | null;
  origem: OrigemDaReleitura;
  resultado: ResultadoDaReleitura;
  ficha_versao: number | null;
  criada_por: string | null;
  criada_em: string;
  conteudo: ConteudoDaReleituraData;
}

export interface ReleituraOutData {
  releitura: ReleituraDaLeituraData;
  reverificadas: number;
}

export interface PedidoDeReleituraDoDocumentoData {
  documento_id: string;
  motivo: string;
}

export interface ReleituraDoDocumentoOutData {
  documento_id: string;
  releituras: ReleituraDaLeituraData[];
  aplicados: CampoDaFichaId[];
  sem_mudanca: CampoDaFichaId[];
  ambiguos: CampoDaFichaId[];
  nao_localizados: CampoDaFichaId[];
  esgotados: CampoDaFichaId[];
  sem_forma: CampoDaFichaId[];
  reverificadas: number;
}

export interface ReleiturasDoCasoOutData {
  case_id: string;
  releituras: ReleituraDaLeituraData[];
}

/** Por que o pedido não foi atendido, pelo código do servidor. */
export type RecusaDaReleitura =
  | 'sem_ficha'
  | 'fonte_nao_encontrada'
  | 'peca_nao_encontrada'
  | 'caso_nao_encontrado'
  | 'sem_protocolo'
  | 'esgotada'
  | 'certificada'
  | 'campo_sem_releitura'
  | 'sem_forma'
  | 'documento_sem_campos'
  | 'fora_do_exame_eletronico'
  | 'peca_concluida'
  | 'verificacao_indisponivel'
  | 'pedido_invalido'
  | 'sem_permissao'
  | 'indisponivel';

export class ReleituraRecusadaError extends Error {
  constructor(readonly recusa: RecusaDaReleitura) {
    super(recusa);
    this.name = 'ReleituraRecusadaError';
  }
}

/** Cada código que a rota devolve, com a recusa que a tela nomeia. */
export const RECUSA_POR_CODIGO: Readonly<Record<string, RecusaDaReleitura>> = {
  FICHA_NOT_FOUND: 'sem_ficha',
  FONTE_NOT_FOUND: 'fonte_nao_encontrada',
  PECA_NOT_FOUND: 'peca_nao_encontrada',
  CASE_NOT_FOUND: 'caso_nao_encontrado',
  CASO_SEM_PROTOCOLO: 'sem_protocolo',
  RELEITURA_ESGOTADA: 'esgotada',
  PECA_CERTIFICADA: 'certificada',
  CAMPO_SEM_RELEITURA: 'campo_sem_releitura',
  VALOR_SEM_FORMA: 'sem_forma',
  DOCUMENTO_SEM_CAMPOS: 'documento_sem_campos',
  CASE_MODALIDADE_INVALIDA: 'fora_do_exame_eletronico',
  EXAME_ELETRONICO_CONCLUIDO: 'peca_concluida',
  EXAME_ELETRONICO_NAO_CONCLUIDO: 'verificacao_indisponivel',
};

const HTTP_FORBIDDEN = 403;
const HTTP_UNPROCESSABLE = 422;

function recusaDe(err: unknown): RecusaDaReleitura {
  if (!axios.isAxiosError(err)) return 'indisponivel';
  const status = err.response?.status;
  if (status === HTTP_FORBIDDEN) return 'sem_permissao';
  const detalhe = (err.response?.data as { detail?: unknown } | undefined)?.detail;
  if (typeof detalhe === 'string') return RECUSA_POR_CODIGO[detalhe] ?? 'indisponivel';
  // 422 sem código: o motivo curto ou o pedido incoerente, recusado pelo schema.
  return status === HTTP_UNPROCESSABLE ? 'pedido_invalido' : 'indisponivel';
}

/** Rever um campo na fonte, ou confirmá-lo com fonte; o que mudou já volta verificado. */
export async function pedirReleitura(
  client: AxiosInstance,
  caseId: string,
  pedido: PedidoDeReleituraData,
): Promise<ReleituraOutData> {
  try {
    const { data } = await client.post<ReleituraOutData>(
      `/casos/${caseId}/ficha-da-leitura/releituras`,
      pedido,
    );
    return data;
  } catch (err) {
    throw new ReleituraRecusadaError(recusaDe(err));
  }
}

/** Rever todos os campos esperados num documento, de uma vez. */
export async function pedirReleituraDoDocumento(
  client: AxiosInstance,
  caseId: string,
  pedido: PedidoDeReleituraDoDocumentoData,
): Promise<ReleituraDoDocumentoOutData> {
  try {
    const { data } = await client.post<ReleituraDoDocumentoOutData>(
      `/casos/${caseId}/ficha-da-leitura/releituras/documento`,
      pedido,
    );
    return data;
  } catch (err) {
    throw new ReleituraRecusadaError(recusaDe(err));
  }
}

/** As releituras já registradas no caso. */
export async function lerReleituras(
  client: AxiosInstance,
  caseId: string,
): Promise<ReleiturasDoCasoOutData> {
  try {
    const { data } = await client.get<ReleiturasDoCasoOutData>(
      `/casos/${caseId}/ficha-da-leitura/releituras`,
    );
    return data;
  } catch (err) {
    throw new ReleituraRecusadaError(recusaDe(err));
  }
}

/** O campo que a tela oferece rever: todos menos os que a Etapa deixou fora. */
export function podeReverLeitura(campo: CampoDaFichaId): boolean {
  return !CAMPOS_SEM_RELEITURA.includes(campo);
}

/** A chave do campo na tela: o campo, e a peça no código declarado. */
export function chaveDaReleitura(campo: CampoDaFichaId, referenteId: string | null): string {
  return referenteId === null ? campo : `${campo}:${referenteId}`;
}

/** O motivo aparado cabe no pedido. */
export function motivoCabe(motivo: string): boolean {
  const tamanho = motivo.trim().length;
  return tamanho >= MOTIVO_MINIMO && tamanho <= MOTIVO_MAXIMO;
}

/** A página do arquivo recebido onde a fonte da releitura está; `null` sem página. */
export function paginaDaFonteDaReleitura(fonte: FonteDaReleituraData): PaginaNaFonte | null {
  if (fonte.pagina_no_original === null) return null;
  return {
    documentoId: fonte.original_id,
    pagina: fonte.pagina_no_original,
    titulo: fonte.documento_nome,
  };
}
